import { IAllocation } from '@kurtosys/types/portal/entity/IAllocation.js';
import { IPortfolioEntityDbRow, TPortfolioGetResponse } from '@kurtosys/types/portal/index.js';
import { IExecutionOptions } from '@kurtosys/types/query/IExecutionOptions.js';
import { IQueryOptionsBreakdownAnalysis } from '@kurtosys/types/query/options/IQueryOptionsBreakdownAnalysis';

import { commonUtils } from '../../common/index.js';

import { BaseQuery } from './base/BaseQuery.js';

export class BreakdownAnalysisQuery extends BaseQuery<IQueryOptionsBreakdownAnalysis> {
	getOptions(): IQueryOptionsBreakdownAnalysis {
		const { breakdownAnalysisOptions, options = {} } = this.queryOptions;
		const response: IQueryOptionsBreakdownAnalysis = {
			code: '',
		};
		if (breakdownAnalysisOptions) {
			Object.assign(response, breakdownAnalysisOptions);
		}
		if (options.breakdownAnalysis) {
			Object.assign(response, options.breakdownAnalysis);
		}
		return response;
	}
	getRawValue<T = any>(executionOptions: IExecutionOptions): T {
		const instance = this.getPortfolioFromContext(executionOptions);
		if (commonUtils.isObject(instance)) {
			let response = this.getPortfolioAllocation(this.options, instance as TPortfolioGetResponse) as any;
			let { order } = this.options;
			if (order) {
				order = order.map((item): any => item.toLowerCase());
				response = commonUtils.sortBy(
					response || [],
					(item: any): any => {
						const label = (item.label || '').toLowerCase();
						if (order) {
							const index = order.indexOf(label);
							if (index > -1) {
								return index.toString();
							}
						}
						return label;
					},
					'ASC',
				);
			}
			return response;
		}
		return undefined;
	}
	getPortfolioAllocation(allocationOptions: IQueryOptionsBreakdownAnalysis, portfolio: TPortfolioGetResponse): any {
		const totalWeight = this.getTotalViableWeightFromPortfolio(allocationOptions, portfolio);
		return this.getCombinedAllocationValues(allocationOptions, portfolio.entities, totalWeight);
	}
	getCombinedAllocationValues(
		allocationOptions: IQueryOptionsBreakdownAnalysis,
		portfolioEntities: IPortfolioEntityDbRow[],
		totalWeight: number,
	): any {
		const allocationsByLabel: { [label: string]: any } = {};
		for (const portfolioEntity of portfolioEntities) {
			const weight = this.getWeight(portfolioEntity);
			const allocation = this.getAllocation(allocationOptions, portfolioEntity);
			if (allocation && allocation.values) {
				allocation.values.forEach((allocationValue): void => {
					const { label, value } = allocationValue;
					const newValue = (value * weight) / totalWeight;
					if (!allocationsByLabel[label]) {
						allocationsByLabel[label] = Object.assign({}, allocationValue, { value: 0 });
					}
					if (allocationsByLabel[label]) {
						allocationsByLabel[label].value += newValue;
					}
				});
			}
		}
		return Object.values(allocationsByLabel);
	}
	getTotalViableWeightFromPortfolio(
		allocationOptions: IQueryOptionsBreakdownAnalysis,
		portfolio: TPortfolioGetResponse,
	): number {
		let total = 0;
		if (portfolio) {
			const { entities } = portfolio;
			if (entities) {
				entities.forEach((portfolioEntity): void => {
					total += this.getTotalViableWeightFromScenarioEntity(allocationOptions, portfolioEntity);
				});
			}
		}
		return total;
	}
	getTotalViableWeightFromScenarioEntity(
		allocationOptions: IQueryOptionsBreakdownAnalysis,
		portfolioEntity: IPortfolioEntityDbRow,
	): number {
		const allocation = this.getAllocation(allocationOptions, portfolioEntity);
		let total = 0;
		if (allocation && allocation.values) {
			const { weight } = portfolioEntity;
			allocation.values.forEach((allocationValue): void => {
				const { value } = allocationValue;
				total += (value * weight) / 100;
			});
		}
		return total;
	}
	getWeight(portfolioEntity: IPortfolioEntityDbRow): number {
		const weight = (portfolioEntity.weight || 100) / 100;
		return weight;
	}
	getAllocation(
		allocationOptions: IQueryOptionsBreakdownAnalysis,
		portfolioEntity: IPortfolioEntityDbRow,
	): IAllocation | undefined {
		const { entity } = portfolioEntity;
		if (entity && !commonUtils.isNullOrEmpty(entity.allocations)) {
			const { code } = allocationOptions;
			const allocation = entity.allocations.find((a): boolean => a.code === code);
			return allocation;
		}
	}
}
